import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

/**
 * How the side navigation shows (F17): an overlay drawer below 1024px, a 72px icon rail from 1024 to 1279px, and
 * the full 248px sidebar from 1280px. Matches Tailwind's `lg` and `xl` breakpoints.
 */
export type SidenavMode = 'drawer' | 'rail' | 'full';

const RAIL_QUERY = '(min-width: 1024px)';
const FULL_QUERY = '(min-width: 1280px)';

@Injectable({
  providedIn: 'root',
})
export class SidenavService {
  private readonly document = inject(DOCUMENT);

  readonly mode = toSignal(
    inject(BreakpointObserver)
      .observe([RAIL_QUERY, FULL_QUERY])
      .pipe(
        map(({ breakpoints }): SidenavMode =>
          breakpoints[FULL_QUERY] ? 'full' : breakpoints[RAIL_QUERY] ? 'rail' : 'drawer',
        ),
      ),
    { initialValue: 'drawer' as SidenavMode },
  );

  /**
   * The drawer (below 1024px) only; the rail and full sidebar are always shown. Starts closed so the drawer never
   * covers a fresh page load.
   */
  private readonly isOpenSignal = signal(false);
  readonly isOpen = this.isOpenSignal.asReadonly();

  /** The menu button that opened the drawer; focus goes back to it on close. */
  private trigger: HTMLElement | null = null;

  constructor() {
    // Widening out of drawer mode closes the drawer; while it's open the page behind doesn't scroll.
    effect(() => {
      if (this.mode() !== 'drawer') this.isOpenSignal.set(false);
    });
    effect(() => {
      this.document.body.classList.toggle('overflow-hidden', this.isOpenSignal());
    });
  }

  open(trigger?: HTMLElement): void {
    this.trigger = trigger ?? null;
    this.isOpenSignal.set(true);
  }

  toggle(trigger?: HTMLElement): void {
    if (this.isOpenSignal()) this.close();
    else this.open(trigger);
  }

  /** Closes the drawer; `restoreFocus` returns focus to the menu button (not after following a link). */
  close(restoreFocus = true): void {
    if (!this.isOpenSignal()) return;
    this.isOpenSignal.set(false);
    if (restoreFocus) this.trigger?.focus();
    this.trigger = null;
  }
}
